import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Active Users Status Chart
 * Donut chart showing active vs inactive users with callout labels
 */
const MBFActiveUsersChart = ({ data = {}, isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Transform API data - API returns: { active_users, inactive_users, active_percentage, inactive_percentage }
    const activePercent = data.active_percentage ?? data.active ?? 78.4;
    const inactivePercent = data.inactive_percentage ?? data.inactive ?? 21.6;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            chartInstance.current = new ChartJS(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Active users', 'Inactive users'],
                    datasets: [
                        {
                            data: [activePercent, inactivePercent],
                            backgroundColor: ['#007bff', '#a6a6a6'],
                            borderWidth: 0,
                            cutout: '65%',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    rotation: -90,
                    circumference: 360,
                    layout: {
                        padding: {
                            left: 60,
                            right: 60,
                            top: 20,
                            bottom: 20,
                        },
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => `${context.label}: ${context.raw}%`,
                            },
                        },
                        datalabels: {
                            display: false,
                        },
                    },
                },
                plugins: [
                    {
                        // Custom plugin to draw callout lines and labels
                        id: 'calloutLabels',
                        afterDraw: (chart) => {
                            const ctx = chart.ctx;
                            const meta = chart.getDatasetMeta(0);
                            const centerX = chart.width / 2;
                            const centerY = chart.height / 2;
                            const data = chart.data.datasets[0].data;

                            meta.data.forEach((arc, index) => {
                                const value = data[index];

                                // Skip drawing callout for segments with 0%
                                if (value <= 0) return;

                                const centerAngle = (arc.startAngle + arc.endAngle) / 2;
                                const outerRadius = arc.outerRadius;

                                // Calculate point on the arc
                                const arcX = centerX + Math.cos(centerAngle) * outerRadius;
                                const arcY = centerY + Math.sin(centerAngle) * outerRadius;

                                // Calculate end point for callout line
                                const lineLength = isMobile ? 20 : 30;
                                const endX = centerX + Math.cos(centerAngle) * (outerRadius + lineLength);
                                const endY = centerY + Math.sin(centerAngle) * (outerRadius + lineLength);

                                // Draw callout line
                                ctx.save();
                                ctx.beginPath();
                                ctx.moveTo(arcX, arcY);
                                ctx.lineTo(endX, endY);

                                // Horizontal extension
                                const horizontalLength = isMobile ? 15 : 25;
                                const horizontalEndX = endX + (Math.cos(centerAngle) > 0 ? horizontalLength : -horizontalLength);
                                ctx.lineTo(horizontalEndX, endY);

                                ctx.strokeStyle = index === 0 ? '#007bff' : '#a6a6a6';
                                ctx.lineWidth = 2;
                                ctx.stroke();
                                ctx.restore();

                                // Draw percentage label
                                const label = index === 0 ? `${activePercent}%` : `${inactivePercent}%`;
                                const labelX = horizontalEndX + (Math.cos(centerAngle) > 0 ? 10 : -10);

                                ctx.save();
                                ctx.fillStyle = index === 0 ? '#006fe6' : '#666666';
                                ctx.font = `500 ${isMobile ? 14 : 18}px "Roboto", sans-serif`;
                                ctx.textAlign = Math.cos(centerAngle) > 0 ? 'left' : 'right';
                                ctx.textBaseline = 'middle';
                                ctx.fillText(label, labelX, endY);
                                ctx.restore();
                            });
                        },
                    },
                ],
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, activePercent, inactivePercent]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Active Users Status
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#fafafa',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '16px',
                        border: '1px solid #d3d3d3',
                        padding: '16px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 0,
                    }}
                >
                    {/* Chart */}
                    <Box
                        sx={{
                            width: '100%',
                            height: { xs: '200px', sm: '220px', md: '240px' },
                            position: 'relative',
                        }}
                    >
                        <canvas ref={chartRef} />
                    </Box>

                    {/* Legend */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: '350px',
                            pt: 3,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Box
                                sx={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    bgcolor: '#007bff',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#666666',
                                    fontFamily: '"Roboto", sans-serif',
                                    fontSize: '18px',
                                    fontWeight: 500,
                                }}
                            >
                                Active users
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Box
                                sx={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    bgcolor: '#a6a6a6',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#666666',
                                    fontFamily: '"Roboto", sans-serif',
                                    fontSize: '18px',
                                    fontWeight: 500,
                                }}
                            >
                                Inactive users
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFActiveUsersChart;
